import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera } from 'lucide-react';

interface FloatingConsultationButtonProps {
  onOpen: () => void;
}

const FloatingConsultationButton: React.FC<FloatingConsultationButtonProps> = ({ onOpen }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      const footer = document.querySelector('footer');
      const footerTop = footer?.getBoundingClientRect().top || window.innerHeight;
      const buttonHeight = 80; // Approximate height of the button + padding

      // Show button when past hero section and hide when near footer
      setIsVisible(
        window.scrollY > heroHeight * 0.8 && 
        footerTop > buttonHeight
      );
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.8 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="fixed bottom-16 right-8 z-40"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <motion.button
            onClick={onOpen}
            className="bg-emerald-500 text-white rounded-full shadow-lg hover:shadow-emerald-500/20 hover:bg-emerald-600 transition-colors flex items-center gap-2 group overflow-hidden"
            style={{
              padding: isHovered ? '0.75rem 1.5rem' : '0.75rem',
              width: isHovered ? 'auto' : '3rem',
            }}
            transition={{ padding: 0.3, width: 0.3 }}
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Camera className="h-5 w-5" />
            </motion.div>
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ 
                opacity: isHovered ? 1 : 0,
                width: isHovered ? 'auto' : 0,
              }}
              transition={{ opacity: 0.2, width: 0.2 }}
              className="whitespace-nowrap font-medium"
            >
              Book Consultation
            </motion.span>
            <motion.div
              className="absolute inset-0 bg-emerald-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0, 0.2, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingConsultationButton; 